import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiPlaneLine, RiMapPinLine, RiCalendarLine, RiStarFill, RiAlertLine, RiMedalLine, RiTrophyLine } from '@remixicon/react';
import { apiClient } from '@/lib/api';
import { TrustScore } from '@/features/trust-score';
import { Card } from '@components/ui/Card';
import { Skeleton } from '@components/ui/Skeleton';
import { TailCard, TailDetailsDrawer } from '@/features/tails';
import { useAppStore } from '@/store/appStore';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos } from '@/features/tails/utils';

const OperatorProfile = ({ operatorId: propOperatorId, isDrawer = false }) => {
  const { id: paramId } = useParams();
  const id = propOperatorId || paramId;
  const navigate = useNavigate();
  const { addToShortlist, clearSelectedTripGroup } = useAppStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTail, setSelectedTail] = useState(null);

  // Fetch operator data
  const { data: operator, isLoading: operatorLoading } = useQuery({
    queryKey: ['operator', id, 'profile'],
    queryFn: () => apiClient.getOperatorById(id, {
      include: 'ntsb_incidents,ucc_verifications,trust_scores,financial_records'
    }),
    enabled: !!id,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Fetch operator's aircrafts
  const { data: tails, isLoading: tailsLoading } = useQuery({
    queryKey: ['operator', id, 'aircrafts'],
    queryFn: async () => {
      const aircraft = await apiClient.getAircrafts(0, 100, { operator_id: id, include: 'make_model' });
      return aircraft.map(a => ({ ...a, operator }));
    },
    enabled: !!id && !!operator,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  const handleDetails = (tailId) => {
    const tail = tails?.find(t => t.tail_number === tailId || t.aircraft_id === tailId || t.id === tailId);
    if (tail) {
      setSelectedTail(tail);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTail(null);
  };

  const handleShortlist = (aircraftId) => {
    const aircraftData = tails?.find(aircraft => aircraft.aircraft_id === aircraftId);
    if (aircraftData) {
      addToShortlist(aircraftData);
    }
    clearSelectedTripGroup();
    navigate('/quotes/generate');
  };

  if (operatorLoading) {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-72 flex-shrink-0">
          <Card className="p-5 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="w-14 h-14 rounded-full" />
              <div>
                <Skeleton className="h-5 w-40 mb-1" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-24 w-full mb-4" />
            <Skeleton className="h-20 w-full mb-4" />
            <Skeleton className="h-32 w-full" />
          </Card>
        </div>
        <div className="flex-1">
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-[420px] rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const fleetCount = tails?.length || operator?.fleet_size || 0;

  return (
    <div className={`flex flex-col lg:flex-row gap-6 ${isDrawer ? 'h-full' : ''} bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6`}>
      {/* Left Sidebar - Operator Info */}
      <div className={`w-full ${isDrawer ? 'lg:w-[420px] lg:h-full lg:overflow-y-auto p-6' : 'lg:w-[520px] lg:sticky lg:top-0 lg:self-start'} flex-shrink-0 space-y-3`}>
        {/* Operator Header Card */}
        <Card className="p-4 rounded-2xl border-0 !shadow-none !bg-transparent">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <RiPlaneLine className="w-7 h-7 text-gray-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">
                {operator?.name || 'Unknown Operator'}
              </h1>
              <p className="text-sm text-gray-500">
                {operator?.faa_city || ''}{operator?.faa_state && `, ${operator.faa_state}`}
              </p>
            </div>
          </div>
        </Card>

        {/* Fleet Score Card - Faded Green Box */}
        <div className="bg-green-50 rounded-full p-4 border border-green-100">
          <div className="flex items-center gap-4 ml-4">
            <TrustScore
              score={operator?.rating_tiers || "Standard"}
              numericScore={operator?.trust_score || 0}
              size="md"
              strokeWidth={8}
            />
            <div>
              <div className="text-xs text-gray-500 mb-2">FLEET SCORE</div>
              <div className="flex flex-wrap gap-1">
                {/*
                <span className="flex items-center gap-1 px-2 py-1 bg-black/10 text-xs rounded-full">
                  <RiPlaneLine className="w-3 h-3" /> {fleetCount} FLEETS
                </span>*/}
                {operator?.argus_rating && operator?.argus_rating !== 'No' && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-black/10 text-xs rounded-full">
                    <RiTrophyLine className="w-3 h-3" /> {operator.argus_rating}
                  </span>
                )}
                {operator?.wyvern_rating && operator?.wyvern_rating !== 'No' && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-black/10 text-xs rounded-full">
                    <RiMedalLine className="w-3 h-3" /> WYVERN WINGMAN
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Cards - Separate White Box */}
        <Card className="p-4 rounded-2xl border-0 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RiStarFill className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <div className="text-md">Excellent</div>
                <div className="text-xs leading-tight">Fleet and Operator Profile</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RiAlertLine className="w-4 h-4 text-yellow-500" />
              </div>
              <div>
                <div className="text-md">Caution</div>
                <div className="text-xs leading-tight">Aircraft Condition and History</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Details Card - Separate White Box */}
        <Card className="p-4 rounded-2xl border-0 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RiMapPinLine className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <div className="text-xs text-gray-500">State</div>
                <div className="text-sm font-semibold text-gray-900">
                  {operator?.faa_city || ''}{operator?.faa_state && `, ${operator.faa_state}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RiCalendarLine className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Registration Date</div>
                <div className="text-sm font-semibold text-gray-900">
                  {operator?.business_started_date
                    ? new Date(operator.business_started_date).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : '-'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RiPlaneLine className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Aircrafts</div>
                <div className="text-sm font-semibold text-gray-900">{fleetCount}</div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Right Content - Aircraft Grid */}
      <div className={`flex-1 ${isDrawer ? 'overflow-y-auto p-6' : ''}`}>
        <h2 className="text-sm mb-4 uppercase text-gray-900 tracking-wide">All Aircrafts</h2>

        {tailsLoading ? (
          <div className={`grid grid-cols-1 ${isDrawer ? '' : 'md:grid-cols-2'} gap-4`}>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-[420px] rounded-2xl" />
            ))}
          </div>
        ) : tails && tails.length > 0 ? (
          <div className={`grid grid-cols-1 ${isDrawer ? '' : 'md:grid-cols-2'} gap-4`}>
            {tails.map((tail) => (
              <TailCard
                key={tail.aircraft_id || tail.id}
                id={tail.aircraft_id || tail.id}
                aircraftId={tail.aircraft_id || tail.id}
                registration={tail.tail_number}
                model={getAircraftModelName(tail)}
                year={tail.mfr_year || '-'}
                passengers={tail.passenger_capacity || tail.max_passengers}
                range = {tail.aircraft_make_model?.min_range && tail.aircraft_make_model?.max_range ? `${tail.aircraft_make_model.min_range} - ${tail.aircraft_make_model.max_range} nm` : '-'}
                operator={operator}
                location={getOperatorWithLocation(operator)}
                trustScore={operator?.trust_score || 0}
                status={tail.current_status || 'Available'}
                image={getAircraftPhotos(tail)}
                features={tail.aircraft_features}
                onDetails={handleDetails}
                onShortlist={handleShortlist}
              />
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center rounded-2xl">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RiPlaneLine className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Aircraft Found</h3>
            <p className="text-sm text-gray-500">This operator has no aircraft on record.</p>
          </Card>
        )}
      </div>

      {/* Tail Details Drawer */}
      <TailDetailsDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        tail={selectedTail}
      />
    </div>
  );
};

export default OperatorProfile;
